import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  addWitnessToWitnessProction,
  editWitnessToWitnessProction,
} from "services/witnessProtectionService";
import { Gender, WitnessForWitnessProtection, WitnessProtection } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  protection: WitnessProtection;
  witness?: WitnessForWitnessProtection;
  inline?: boolean;
  onClose: (refetch: boolean) => void;
};

const inputClass =
  "w-full border border-gray-300 rounded-lg bg-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-1";

const WitnessForm: FC<Props> = ({ protection, witness, inline, onClose }) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<WitnessForWitnessProtection>({
    witness_full_name: "",
    // witness_age: 0,
    witness_gender: "Male",
    witness_region: "",
    witness_zone: "",
    witness_woreda: "",
    witness_phone_number: "",
    protection_request: protection.id ?? 0,
  });

  useEffect(() => {
    if (witness) {
      setFormState(witness);
    }
  }, [witness]);

  const handleChange = (e: { target: { name: any; value: any } }) => {
    setFormState({
      ...formState,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = () => {
    if (!formState?.witness_full_name)
      return `${t("witness_protection.modals.witness_form.full_name")} ${t("required")}.`;
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const api = witness
      ? editWitnessToWitnessProction
      : addWitnessToWitnessProction;
    api({
      ...formState,
      id: witness?.id ?? undefined,
      admin_unit: loginData?.admin_unit?.toString() ?? "",
    })
      .then(() => {
        showTooltip(
          witness ? t("successfully_updated") : t("successfully_submitted"),
          "success",
        );
        onClose(true);
        setLoading(false);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const fields = (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <label className={labelClass}>
          {t("witness_protection.modals.witness_form.full_name")}{" "}
          <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className={inputClass}
          name="witness_full_name"
          value={formState?.witness_full_name}
          onChange={(e) =>
            setFormState({
              ...formState,
              witness_full_name: e.target.value,
            })
          }
        />
      </div>
      <div>
        <label className={labelClass}>
          {t("witness_protection.modals.witness_form.age")}
        </label>
        <input
          type="number"
          className={inputClass}
          name="witness_age"
          value={formState?.witness_age}
          onChange={handleChange}
        />
      </div>
      <div>
        <label className={labelClass}>
          {t("witness_protection.modals.witness_form.gender")}
        </label>
        <select
          className={`${inputClass} capitalize`}
          name="witness_gender"
          value={formState?.witness_gender ?? ""}
          onChange={handleChange}
        >
          <option value={""}>
            {t("witness_protection.modals.witness_form.select_gender")}
          </option>
          {Object.keys(Gender).map((key) => (
            <option key={key} value={key}>
              {Gender[key as keyof typeof Gender]}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelClass}>
          {t("witness_protection.modals.witness_form.region")}
        </label>
        <input
          className={inputClass}
          name="witness_region"
          value={formState?.witness_region}
          onChange={handleChange}
        />
      </div>
      <div>
        <label className={labelClass}>
          {t("witness_protection.modals.witness_form.zone")}
        </label>
        <input
          className={inputClass}
          name="witness_zone"
          value={formState?.witness_zone}
          onChange={handleChange}
        />
      </div>
      <div>
        <label className={labelClass}>
          {t("witness_protection.modals.witness_form.woreda")}
        </label>
        <input
          className={inputClass}
          name="witness_woreda"
          value={formState?.witness_woreda}
          onChange={handleChange}
        />
      </div>
      <div>
        <label className={labelClass}>
          {t("witness_protection.modals.witness_form.phone_number")}
        </label>
        <input
          className={inputClass}
          name="witness_phone_number"
          value={formState?.witness_phone_number}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const submitLabel = loading ? (
    <span className="flex items-center justify-center gap-2">
      {LoadingIcon}
      {t("loading")}
    </span>
  ) : witness ? (
    t("witness_protection.modals.witness_form.edit")
  ) : (
    t("witness_protection.modals.witness_form.submit")
  );

  // Inline mode: render as a card inside the page (e.g. the right panel).
  if (inline) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 shrink-0 rounded-lg bg-gradient-to-br from-purple-50 to-purple-100 flex items-center justify-center text-purple-600 text-base">
              👤
            </div>
            <h3 className="text-base font-semibold text-gray-900">
              {witness
                ? t("witness_protection.modals.witness_form.edit_title")
                : t("witness_protection.modals.witness_form.new_title")}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            aria-label={t("cancel", { defaultValue: "Cancel" })}
          >
            ×
          </button>
        </div>
        <form className="p-5 space-y-4" onSubmit={handleSubmit}>
          {fields}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </button>
            <button
              type="submit"
              className={`px-6 py-2 text-sm font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
              disabled={loading}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {witness
            ? t("witness_protection.modals.witness_form.edit_title")
            : t("witness_protection.modals.witness_form.new_title")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("witness_protection.modals.witness_form.form_description")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {fields}
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WitnessForm;
